import React, { useState } from 'react';
import { FaCopy, FaWifi, FaUser, FaDollarSign } from 'react-icons/fa';

// Compact, read-only popup content shown inside a Leaflet marker Popup on
// the Customer Map. Deliberately not the full CustomerDetailsModal used on
// the Customers tab — that component always mounts ProvisioningSection /
// PricingOverrideForm / ConsentsSection, which call write endpoints
// (markProvisioned, setCustomerPricingOverride, recordAdminConsent, etc.)
// unconditionally. This panel only ever reads fields already present on the
// customer object — no write actions, no mutation calls.

const CopyChip = ({ value, label }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="text-[#928374] italic">—</span>;
  const copy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1000);
    } catch { /* ignore — fallback browsers */ }
  };
  return (
    <button
      onClick={copy}
      title={`Copy ${label || 'value'}`}
      className="inline-flex items-center gap-1 text-[#ebdbb2] hover:text-[#fabd2f] font-mono text-xs"
    >
      {value}
      {copied ? <span className="text-[#b8bb26]">✓</span> : <FaCopy size={9} className="text-[#928374]" />}
    </button>
  );
};

const Row = ({ label, children }) => (
  <div className="flex items-start justify-between gap-3 py-1 border-b border-[#3c3836] last:border-0">
    <span className="text-[#928374] text-xs shrink-0">{label}</span>
    <span className="text-[#ebdbb2] text-xs text-right">{children ?? <span className="text-[#928374] italic">—</span>}</span>
  </div>
);

const SectionHeader = ({ icon, title }) => (
  <div className="flex items-center gap-1.5 text-[#fabd2f] text-xs font-semibold uppercase tracking-wide mt-3 mb-1 first:mt-0">
    {icon} {title}
  </div>
);

const formatDate = (value) => {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return value;
  }
};

const STATUS_STYLE = {
  approved: 'bg-[#b8bb26] text-[#1d2021]',
  active: 'bg-[#b8bb26] text-[#1d2021]',
  pending: 'bg-[#fabd2f] text-[#1d2021]',
  rejected: 'bg-[#fb4934] text-[#1d2021]',
  cancelled: 'bg-[#fb4934] text-[#1d2021]',
};

const CustomerMapDetailPanel = ({ customer, precise }) => {
  const address = customer.address ||
    [customer.street, customer.suburb, customer.state, customer.postcode].filter(Boolean).join(', ') ||
    'No address on file';

  return (
    <div className="w-[280px] max-h-[60vh] overflow-y-auto customer-popup-scroll">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-[#ebdbb2] font-semibold text-sm truncate">{customer.fullName || customer.email}</span>
        <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase shrink-0 ${STATUS_STYLE[customer.status] || 'bg-[#3c3836] text-[#ebdbb2]'}`}>
          {customer.status}
        </span>
      </div>
      <p className="text-[#928374] text-xs mb-1">{address}</p>
      {precise === false && (
        <p className="text-[#fabd2f] text-[10px] mb-1">
          ~ Approximate location — exact address not found, pin is near the street/suburb only
        </p>
      )}

      <SectionHeader icon={<FaUser size={10} />} title="Customer" />
      <Row label="Email">{customer.email}</Row>
      <Row label="Phone">{customer.phone}</Row>
      <Row label="Account #">{customer.accountNumber}</Row>
      <Row label="Service Start">{customer.serviceStartDate === 'asap' ? 'ASAP' : formatDate(customer.serviceStartDate || customer.serviceDate)}</Row>

      <SectionHeader icon={<FaWifi size={10} />} title="Connection" />
      <Row label="Plan">{customer.plan}</Row>
      <Row label="Access Tech">{customer.vocusSqSnapshot?.accessTechnology}</Row>
      <Row label="Attainable Speed">{customer.vocusSqSnapshot?.attainableSpeed}</Row>
      <Row label="LOC"><CopyChip value={customer.vocusSqSnapshot?.nbnLocationId} label="LOC" /></Row>
      <Row label="AVC ID"><CopyChip value={customer.avcId} label="AVC ID" /></Row>
      <Row label="Service ID"><CopyChip value={customer.vocusServiceId} label="Service ID" /></Row>
      <Row label="Provision Status">{customer.provisionStatus}</Row>

      <SectionHeader icon={<FaDollarSign size={10} />} title="Billing" />
      <Row label="Billing Status">{customer.billingStatus}</Row>
      <Row label="Monthly Fee">{customer.monthlyFee != null ? `$${customer.monthlyFee}` : null}</Row>
      <Row label="Next Billing">{formatDate(customer.nextBillingDate)}</Row>
      <Row label="Payment Method">{customer.defaultPaymentMethodType}</Row>
    </div>
  );
};

export default CustomerMapDetailPanel;
